/**
 * PATH: apps/quote/src/components/builder/SetupHintBanner.tsx
 * FILE_ID: QUOTE-BUILDER-SETUP-HINT-001
 * PURPOSE: Empty-state banner that points new estimators at the Setup tab.
 * WHY: User feedback "setup is missing" — the Setup tab existed (labelled
 *      "Config") but wasn't surfaced. This banner appears ABOVE the grid
 *      whenever the quote has zero line items AND the config is missing
 *      core rate values, nudging the operator to set rates before entering
 *      lines so the auto-compute is meaningful.
 *
 * FLOW:
 *   ┌─────────────────────────┐    ┌──────────────────┐    ┌──────────┐
 *   │ Quote.config + sections │ →  │  shouldShowHint  │ →  │  Banner  │
 *   │ (no items? config thin?)│    │  (pure)          │    │          │
 *   └─────────────────────────┘    └──────────────────┘    └──────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react (icons)
 *   - @/lib/utils cn
 * RELATED:
 *   - apps/quote/src/components/builder/sheets/AssumptionsSheet.tsx (target)
 */

import { Settings, ArrowRight, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import type { QuoteDetail } from '@/lib/quoteApi'

interface SetupHintBannerProps {
  quote: QuoteDetail
  /** Total line items across all sections — banner only shows when 0. */
  totalLineItems: number
  /** Activate the Setup tab (wired to useWorkspace.activateAssumptions). */
  onOpenSetup: () => void
  className?: string
}

/**
 * Minimum config keys we expect to be filled before the operator starts
 * entering line items. If any of these are missing or zero, the auto-compute
 * in the grid won't be meaningful and the operator will think "formulas are
 * missing". Showing the banner short-circuits that confusion.
 */
const REQUIRED_CONFIG_KEYS = [
  'labourCostRate',
  'labourChargeOutRate',
  'hoursPerDay',
] as const

/**
 * Pure: should the banner show right now?
 *   - Show only when there are zero line items (otherwise it's noise).
 *   - Show only when at least one required config key is unset/zero.
 */
export function shouldShowSetupHint(quote: QuoteDetail, totalLineItems: number): boolean {
  if (totalLineItems > 0) return false
  const cfg = (quote.config ?? {}) as Record<string, unknown>
  const missing = REQUIRED_CONFIG_KEYS.some((k) => {
    const v = cfg[k]
    if (v === null || v === undefined || v === '') return true
    const n = Number(v)
    return !Number.isFinite(n) || n === 0
  })
  return missing
}

export default function SetupHintBanner({ quote, totalLineItems, onOpenSetup, className }: SetupHintBannerProps) {
  // Per-quote dismissal: if user explicitly dismisses the banner, don't nag
  // them again on this quote in this session. Persisted to sessionStorage so
  // a refresh re-shows it but a tab close-and-reopen doesn't.
  const dismissKey = `qb-setup-hint-dismissed:${quote.id}`
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    return sessionStorage.getItem(dismissKey) === '1'
  })

  // Re-evaluate when the quote / count changes — hide banner once items exist
  // (we'd already return null below, but keep the effect for symmetry).
  useEffect(() => {
    if (totalLineItems > 0) setDismissed(false)
  }, [totalLineItems])

  if (dismissed) return null
  if (!shouldShowSetupHint(quote, totalLineItems)) return null

  const handleDismiss = () => {
    setDismissed(true)
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(dismissKey, '1')
    }
  }

  return (
    <div
      className={cn(
        'mx-3 mt-2 mb-1 flex items-center gap-3 px-3 py-2 rounded-lg border',
        'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
        'text-amber-900 dark:text-amber-200 text-sm',
        className,
      )}
      role="status"
    >
      <Settings className="w-4 h-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 leading-tight">
        <span className="font-medium">Set your rates first.</span>{' '}
        <span className="opacity-80">
          Open Setup to confirm labour cost, chargeout rate, and hours/day so the
          grid computes Hrs / Days / Margin correctly as you type.
        </span>
      </div>
      <button
        onClick={onOpenSetup}
        className={cn(
          'flex-shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded font-medium text-xs',
          'bg-amber-600 hover:bg-amber-700 text-white transition-colors',
        )}
      >
        Open Setup
        <ArrowRight className="w-3 h-3" />
      </button>
      <button
        onClick={handleDismiss}
        title="Dismiss"
        className="flex-shrink-0 p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
